import { isHidden } from '@tabel/shared/form-fields';
import {
  type TraceRowPublic,
  type TraceStep,
  type TraceStepInfo,
  traceStepKey,
} from '@tabel/shared/schemas/day-trace';
import { ListTree } from 'lucide-react';
import { EmptyState } from './ui';

/*
 * Трасса расчёта дня для разработчика (MVP C4; data-model.md А.8, `TraceRowPublic`; ADR-0001 §11). Сервер отдаёт только
 * строки выбранной даты с изменениями, подряд идущие однотипные строки шага уже свёрнуты в одну (`count`, фраза со
 * счётом). Строки идут по порядку исполнения; подряд идущие строки одного шага (кубик, часть, версия реализации) —
 * одна группа: в заголовке — код и название кубика по каталогу, версия, число операций, прочитанные параметры набора с
 * подписями (без подписи — имя) и уровень правил — один раз на группу. Суммы Δ у группы нет: Δ операций одного шага
 * складывать нельзя (K1 размечает отрезок и пишет строку теми же минутами). В строке — номер, операция (свёрнутая —
 * «×N»), фраза и Δ в минутах.
 *
 * Фразу строит сервер по шаблону причины шага: интерфейс её не собирает и не правит. Значение с данными людей
 * (`{ hidden: true }`) показывается подписью в фигурных скобках, `{подпись}`. Кода под конкретный кубик нет (SC-014).
 */

/**
 * Изменение в минутах: мс → минуты, половина вверх (А.8) — к +∞ и у отрицательных: −1,5 мин → −1.
 * TODO(T052): минуты `previews.summary.deltaByCode` на сервере округляются так же (data-model, `previews`); при
 * реализации отчёта влияния перенести эту функцию в `@tabel/shared` и звать её и на сервере, и здесь.
 */
export function deltaMinutes(deltaMs: number): number {
  const m = Math.floor(deltaMs / 60_000 + 0.5);
  return m === 0 ? 0 : m;
}

function showDelta(deltaMs: number): string {
  const m = deltaMinutes(deltaMs);
  if (m > 0) return `+${m}`;
  if (m < 0) return `−${-m}`;
  return '—';
}

/** Операции ядра (А.3) словами; новой операции нет в списке — код. */
const OP_LABEL: Readonly<Record<string, string>> = {
  markPass: 'пометка прохода',
  addDerivedPunch: 'достроена отметка',
  formShift: 'смена собрана',
  setShiftDate: 'дата смены',
  matchPlan: 'привязка к плану',
  planShift: 'плановая смена',
  planEdit: 'правка плана',
  paint: 'отрезок времени',
  relabelKind: 'смена вида',
  addLabel: 'метка',
  removeLabel: 'метка снята',
  snapEdge: 'сдвиг края',
  projectRow: 'строка табеля',
  roundLine: 'округление строки',
  annotate: 'значение шага',
  flag: 'пометка',
  setBaseKind: 'базовый вид',
  setDayCode: 'код дня',
  setCounter: 'счётчик',
  allocate: 'разнесение',
  noop: 'без изменений',
};

/** Значение параметра: строка, число, да/нет, список через запятую; скрытое — `{подпись}`. */
function plain(v: unknown, label: string): string {
  if (isHidden(v)) return `{${label}}`;
  if (v === undefined || v === null) return '—';
  if (typeof v === 'boolean') return v ? 'да' : 'нет';
  if (typeof v === 'number') return String(v).replace('.', ',');
  if (typeof v === 'string') return v;
  if (Array.isArray(v)) return v.map((x) => plain(x, label)).join(', ');
  return `{ ${Object.entries(v as Record<string, unknown>)
    .map(([k, x]) => `${k}: ${plain(x, k)}`)
    .join(', ')} }`;
}

/** Код шага: кубики через «+» и часть. */
export function stepCode(step: TraceStep): string {
  const cubes = step.cubes.join('+');
  return step.part ? `${cubes} · ${step.part}` : cubes;
}

/** Группа трассы: подряд идущие строки одного шага. */
export interface TraceGroup {
  readonly key: string;
  readonly step: TraceStep;
  readonly impl: number;
  readonly rows: readonly TraceRowPublic[];
}

/** Подряд идущие строки одного шага и версии — одна группа; шаг встретился снова (другая смена) — новая. */
export function groupTrace(rows: readonly TraceRowPublic[]): TraceGroup[] {
  const out: { key: string; step: TraceStep; impl: number; rows: TraceRowPublic[] }[] = [];
  for (const r of rows) {
    const key = traceStepKey(r.step, r.impl);
    const last = out.at(-1);
    if (last?.key === key) last.rows.push(r);
    else out.push({ key, step: r.step, impl: r.impl, rows: [r] });
  }
  return out;
}

/** «1 операция», «2 операции», «5 операций». */
function opsCount(n: number): string {
  const d10 = n % 10;
  const d100 = n % 100;
  const word =
    d10 === 1 && d100 !== 11
      ? 'операция'
      : d10 >= 2 && d10 <= 4 && (d100 < 12 || d100 > 14)
        ? 'операции'
        : 'операций';
  return `${n} ${word}`;
}

function paramsText(
  params: Readonly<Record<string, unknown>>,
  labels: Readonly<Record<string, string>>,
): string {
  return Object.entries(params)
    .map(([name, v]) => {
      const label = labels[name] ?? name.replaceAll('_', ' ');
      return `${label}: ${plain(v, label)}`;
    })
    .join(' · ');
}

const TH = 'px-3 py-2 text-left text-[12px] font-semibold text-ink-2 whitespace-nowrap';
const TD = 'px-3 py-1.5 align-top';
const COLS = 4;

/** Таблица трассы по шагам. `steps` — название кубика и подписи параметров по ключу шага (`traceStepKey`). */
export function TraceTable({
  rows,
  steps,
}: {
  rows: readonly TraceRowPublic[];
  steps: Readonly<Record<string, TraceStepInfo>>;
}) {
  if (rows.length === 0) return <EmptyState icon={ListTree} title="Строк трассы нет" />;
  return (
    <div className="overflow-x-auto rounded-control border border-line">
      <table className="w-full text-[12.5px] text-ink">
        <thead className="bg-subtle">
          <tr>
            <th className={`${TH} w-10 text-right`}>№</th>
            <th className={TH}>Операция</th>
            <th className={TH}>Что сделано</th>
            <th className={`${TH} text-right`}>Δ, мин</th>
          </tr>
        </thead>
        {groupTrace(rows).map((g) => {
          const info = Object.hasOwn(steps, g.key) ? steps[g.key] : undefined;
          const title = info?.title && info.title !== g.step.cubes.join(' + ') ? info.title : null;
          const head = g.rows[0];
          const params = head ? paramsText(head.params, info?.labels ?? {}) : '';
          const p = head?.provenance ?? null;
          const ops = g.rows.reduce((n, r) => n + r.count, 0);
          return (
            <tbody key={`${g.key}-${head?.seq}`} className="border-t border-line">
              <tr className="bg-subtle/60">
                <th scope="rowgroup" colSpan={COLS} className="px-3 py-1.5 text-left font-normal">
                  <span className="font-semibold text-ink">{stepCode(g.step)}</span>
                  {title && <span className="ml-2 text-ink-2">{title}</span>}
                  <span className="ml-2 text-[12px] text-ink-3 tabular-nums">
                    {`v${g.impl} · ${opsCount(ops)}`}
                    {p ? ` · ${p.level}${p.versionId !== null ? `, версия набора ${p.versionId}` : ''}` : ''}
                  </span>
                  {params && <span className="block text-[12px] text-ink-3">{params}</span>}
                </th>
              </tr>
              {g.rows.map((r) => (
                <tr key={r.seq} className="border-t border-line/60">
                  <td className={`${TD} text-right tabular-nums text-ink-3`}>{r.seq}</td>
                  <td className={`${TD} whitespace-nowrap text-ink-2`} title={r.op}>
                    {OP_LABEL[r.op] ?? r.op}
                    {r.count > 1 && <span className="ml-1 tabular-nums text-ink-3">{`×${r.count}`}</span>}
                  </td>
                  <td className={TD}>{r.phrase || '—'}</td>
                  <td className={`${TD} text-right tabular-nums`}>{showDelta(r.deltaMs)}</td>
                </tr>
              ))}
            </tbody>
          );
        })}
      </table>
    </div>
  );
}
